import { useEffect, useRef, useState } from 'react'
import { Avatar } from '../../components/Avatar'
import { AvatarCropOverlay } from '../../components/AvatarCropOverlay'
import { looksLikeHeic } from '../../lib/heic'
import { TrophyShelf } from '../../components/TrophyShelf'
import { SectionTitle } from '../../components/SectionTitle'
import { ChevronRight } from '../../components/ChevronRight'
import { VersionStamp } from '../../components/VersionStamp'
import { ScreenHeader } from '../../components/ScreenHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import {
  fetchMyTrophies, fetchMyStampsPanel, fetchProfileActivity, fetchProfileStats, uploadProfileAvatar,
  type StampsPanel,
  type ProfileActivity, type ProfileStats, type Trophy,
} from '../../lib/api/profile'
import { manageSubscription } from '../../lib/api/billing'
import { StampsInfoOverlay } from './StampsInfoOverlay'
import { clearTipFlags } from '../../lib/api/userFlags'
import { useAuth } from '../../lib/auth'
import { useT, useI18n } from '../../i18n'
import { useSubscription } from '../../lib/useSubscription'
import { readError } from '../../lib/errors'
import type { Profile } from '../types'

interface Props {
  profile: Profile
  onEditProfile: () => void
  onOpenNotifications: () => void
  onOpenLanguage: () => void
  onOpenPrivacy: () => void
  onOpenHelp: () => void
  onSignOut: () => void
  /** Tap a trophy to open the weekly results it was won in. */
  onOpenTrophy: (trophy: Trophy) => void
}

const GAME_THUMB: Record<ProfileActivity['game_id'], string> = {
  ideatthat:      '/games/ideatthat.webp',
  colorful:       '/games/colorful.webp',
  'tokyo-metro':  '/games/tokyo-metro.webp',
  'three-step':   '/games/three-step.webp',
  'fake-out':     '/games/fake-out.webp',
  'close-enough': '/games/close-enough.webp',
}

export function ProfileScreen({
  profile, onEditProfile,
  onOpenNotifications, onOpenLanguage, onOpenPrivacy, onOpenHelp, onSignOut, onOpenTrophy,
}: Props) {
  const auth = useAuth()
  const t = useT()
  const { lang } = useI18n()
  const sub = useSubscription()
  const { updateProfile } = auth
  const linkedProviders = auth.linkedProviders()
  const hasApple = linkedProviders.includes('apple')
  const hasGoogle = linkedProviders.includes('google')
  const hasEmail = linkedProviders.includes('email')
  const [linkingApple, setLinkingApple] = useState(false)
  const [linkingGoogle, setLinkingGoogle] = useState(false)
  const [linkEmailOpen, setLinkEmailOpen] = useState(false)
  const [linkEmailDraft, setLinkEmailDraft] = useState({ email: '', password: '' })
  const [linkEmailBusy, setLinkEmailBusy] = useState(false)
  const [linkErr, setLinkErr] = useState<string | null>(null)
  // "Show game tips again" — clears the tip:* flags so each weekly
  // explainer overlay shows once more on its next open.
  const [tipsReset, setTipsReset] = useState(false)
  const [stampsInfoOpen, setStampsInfoOpen] = useState(false)

  async function handleLinkApple() {
    setLinkingApple(true); setLinkErr(null)
    try { await auth.linkApple() }
    catch (e) { setLinkErr(readError(e)) }
    finally { setLinkingApple(false) }
  }

  async function handleLinkGoogle() {
    setLinkingGoogle(true); setLinkErr(null)
    try { await auth.linkGoogle() }
    catch (e) { setLinkErr(readError(e)) }
    finally { setLinkingGoogle(false) }
  }

  async function handleLinkEmail() {
    setLinkEmailBusy(true); setLinkErr(null)
    try {
      await auth.linkEmailPassword(linkEmailDraft.email, linkEmailDraft.password)
      setLinkEmailOpen(false)
      setLinkEmailDraft({ email: '', password: '' })
    } catch (e) { setLinkErr(readError(e)) }
    finally { setLinkEmailBusy(false) }
  }

  async function handleUnlink(provider: 'apple' | 'google' | 'email') {
    setLinkErr(null)
    const confirmMsg = provider === 'apple'
      ? t('Remove Apple sign-in?')
      : provider === 'google'
        ? t('Remove Google sign-in?')
        : t('Remove email sign-in?')
    if (!window.confirm(confirmMsg)) return
    try { await auth.unlinkProvider(provider) }
    catch (e) { setLinkErr(readError(e)) }
  }

  const [stats, setStats] = useState<ProfileStats | null>(null)
  const [activity, setActivity] = useState<ProfileActivity[] | null>(null)
  const [trophies, setTrophies] = useState<Trophy[] | null>(null)
  const [stampsPanel, setStampsPanel] = useState<StampsPanel | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [cropSource, setCropSource] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Same two gates as the edit screen, so the checks don't drift apart
  // depending on which avatar the user happened to tap.
  async function handleFile(file: File) {
    setError(null)
    if (!file.type.startsWith('image/') && !(await looksLikeHeic(file))) {
      setError(t('That file does not look like an image.'))
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t('Pick an image under 10 MB.'))
      return
    }
    setCropSource(file)
  }

  async function handlePickAvatar(file: File) {
    setUploading(true); setError(null)
    try {
      const url = await uploadProfileAvatar(profile.id, file)
      await updateProfile({ avatar_url: url })
    } catch (e) {
      setError(readError(e))
    } finally {
      setUploading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    Promise.all([
      fetchProfileStats(profile.id),
      fetchProfileActivity(profile.id, 8),
      fetchMyTrophies(profile.id),
      fetchMyStampsPanel(),
    ]).then(([s, a, t, sp]) => {
      if (cancelled) return
      setStats(s); setActivity(a); setTrophies(t); setStampsPanel(sp)
    }).catch(e => {
      if (!cancelled) setError(readError(e))
    })
    return () => { cancelled = true }
  }, [profile.id])

  return (
    <div className="screen pb-24">
      <BackgroundTitle text="Profile" color="#D9E026" />
      <ScreenHeader
        title={t('PROFILE')}
        titleColorClass="text-brand-lime-dark"
        rightSlot={
          <button onClick={onEditProfile} className="btn-ghost py-1.5">{t('Edit')}</button>
        }
      />

      <div className="px-5 space-y-6 relative z-10">
        <div className="card p-5">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="relative group active:scale-95 transition-transform
                         disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label={t('Change profile photo')}
            >
              <Avatar
                name={profile.display_name}
                avatarUrl={profile.avatar_url}
                size="xl"
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full
                               bg-brand-text text-white text-xs flex items-center
                               justify-center shadow-card border-2 border-white">
                {uploading ? '…' : '✎'}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={e => {
                const f = e.target.files?.[0]
                if (f) void handleFile(f)
                if (e.target) e.target.value = ''
              }}
            />
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl font-bold text-brand-text truncate">
                {profile.display_name}
              </h2>
              {profile.subscription_status !== 'active' ? (
                <span className="text-xs text-brand-muted mt-1 block">{t('No subscription. See All-Access on the Games tab.')}</span>
              ) : sub.source === 'stamps' ? (
                // Access is traded stamp time only: nothing to manage,
                // nothing renews, nothing charges. A Manage link here
                // would dead-end in Apple/Stripe confusion.
                <div className="mt-1">
                  <span className="text-xs text-brand-gold font-semibold block">
                    {sub.currentPeriodEnd
                      ? t('App time from stamps · until {date}', { date: shortDate(sub.currentPeriodEnd) })
                      : t('App time from stamps')}
                    <StampsInfoButton onOpen={() => setStampsInfoOpen(true)} />
                  </span>
                  <span className="text-[11px] text-brand-muted block mt-0.5">{t('Traded from your stamps on the website. It never renews and never charges anything.')}</span>
                </div>
              ) : (
                <div className="mt-1">
                  <button
                    type="button"
                    onClick={() => manageSubscription(sub.source).catch(e => setError(readError(e)))}
                    className="text-xs text-brand-gold font-semibold
                               underline underline-offset-2 active:scale-95 transition-transform"
                  >{t('All-Access Pass · Manage')}</button>
                  {/* Store subscription with granted time banked. Say
                      QUEUED, not "covered through {date}": the store
                      keeps renewing (and charging) until the user
                      cancels, so a blended end date would wrongly
                      imply the sub fades into stamp time on its own.
                      Once the paid period is actually over
                      (storePeriodEnd in the past) the granted tail is
                      what's covering, so flip to "in use". */}
                  {(() => {
                    const unused = (sub.stampGrants ?? []).filter(g => !g.used)
                    if (unused.length === 0) return null
                    const stampTimeInUse =
                      sub.storePeriodEnd != null && new Date(sub.storePeriodEnd) <= new Date()
                    if (stampTimeInUse) {
                      return (
                        <span className="text-[11px] text-brand-muted block mt-0.5">
                          {sub.currentPeriodEnd
                            ? t('Stamp time in use · until {date}', { date: shortDate(sub.currentPeriodEnd) })
                            : t('Stamp time in use')}
                          <StampsInfoButton onOpen={() => setStampsInfoOpen(true)} />
                        </span>
                      )
                    }
                    const years = unused.filter(g => g.period === 'year').length
                    const months = unused.filter(g => g.period === 'month').length
                    const parts = [
                      years > 0 ? (years === 1 ? t('1 year') : t('{n} years', { n: years })) : null,
                      months > 0 ? (months === 1 ? t('1 month') : t('{n} months', { n: months })) : null,
                    ].filter(Boolean).join(', ')
                    return (
                      <span className="text-[11px] text-brand-muted block mt-0.5">
                        {t('+{parts} of stamp time queued', { parts })}
                        <StampsInfoButton onOpen={() => setStampsInfoOpen(true)} />
                      </span>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Real stats — null while the RPC is in flight, zeros for
              brand-new accounts. */}
          <div className="flex gap-0 mt-5 pt-5 border-t border-brand-border">
            <Stat label={t('Games Played')}   value={stats?.games_played ?? null} />
            <Stat label={t('Weekly Events')}  value={stats?.weekly_events_entered ?? null} />
            <Stat label={t('Votes Received')} value={stats?.votes_received ?? null} />
          </div>
        </div>

        <div>
          <SectionTitle className="mb-3">{t('Trophy Shelf')}</SectionTitle>
          <div className="card p-4">
            {trophies === null ? (
              <div className="space-y-3">
                <div className="h-24 rounded-sm bg-brand-elevated animate-pulse" />
              </div>
            ) : (
              <TrophyShelf trophies={trophies} onOpenTrophy={onOpenTrophy} />
            )}
          </div>
        </div>

        {/* Stamps — earned BY PLAYING only. Spending happens on the
            website and never lowers this number, so it reads as a
            lifetime tally, not a balance. */}
        <div>
          <SectionTitle className="mb-3">{t('Stamps')}</SectionTitle>
          <div className="card p-4">
            {stampsPanel === null ? (
              <div className="h-24 rounded-sm bg-brand-elevated animate-pulse" />
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-3xl font-black text-brand-text">
                    {stampsPanel.weekly_stamps + (stampsPanel.bonus_awarded ? 1 : 0)}
                  </span>
                  <span className="text-sm font-semibold text-brand-text">
                    {stampsPanel.weekly_stamps + (stampsPanel.bonus_awarded ? 1 : 0) === 1
                      ? t('stamp collected by playing')
                      : t('stamps collected by playing')}
                  </span>
                </div>
                <p className="text-xs text-brand-muted mt-1">{t("This number only reflects the stamps you've earned by playing in the app, it does not reflect your account balance on the website.")}</p>

                <p className="text-xs text-brand-muted mt-3">{t('Stamps are the Jordan Draper Games reward points. On the website you can trade 3 for a month of All-Access (20 for a year), or take $1 off any order per stamp.')}</p>
                <a
                  href="https://jordandraper.com/account"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-1.5 text-xs font-semibold text-brand-gold
                             underline underline-offset-2 active:scale-95 transition-transform"
                >{t('Manage your stamps on jordandraper.com')}</a>

                <div className="mt-4 pt-4 border-t border-brand-border">
                  <p className="text-xs font-semibold text-brand-text uppercase tracking-wide">{t('App starter bonus')}</p>
                  <p className="text-xs text-brand-muted mt-0.5 mb-3">{t("Earn one bonus stamp when you play one game of Colorful, I'D EAT THAT, and THREE STEP SOLUTION.")}</p>
                  <div className="flex items-start gap-4">
                    <StarterCircle
                      reward
                      done={stampsPanel.bonus_awarded}
                      label={stampsPanel.bonus_awarded ? 'Earned' : 'Bonus'}
                    />
                    <div className="w-px self-stretch bg-brand-border" />
                    <StarterCircle done={stampsPanel.colorful_done} label="COLORFUL" />
                    <StarterCircle done={stampsPanel.iet_done} label={"I'D EAT THAT"} />
                    <StarterCircle done={stampsPanel.three_step_done} label={t('THREE STEP')} />
                  </div>

                  {/* Trophy chases — one bonus stamp per completed row.
                      Circles fill with the grey stamp per qualifying
                      weekly trophy (solo-human weeks never count). */}
                  <ChallengeRow
                    title={t("Win 5 I'D EAT THAT weekly trophies")}
                    count={stampsPanel.iet_trophies}
                    done={stampsPanel.iet5_done}
                  />
                  <ChallengeRow
                    title={t('Win 5 COLORFUL weekly trophies')}
                    count={stampsPanel.colorful_trophies}
                    done={stampsPanel.colorful5_done}
                  />
                  <ChallengeRow
                    title={t('Win 5 THREE STEP SOLUTION weekly trophies')}
                    count={stampsPanel.three_step_trophies}
                    done={stampsPanel.three_step5_done}
                  />
                  <ChallengeRow
                    title={t('Win 5 FAKE OUT weekly trophies')}
                    count={stampsPanel.fake_out_trophies}
                    done={stampsPanel.fake_out5_done}
                  />
                  <ChallengeRow
                    title={t('Win 5 CLOSE ENOUGH weekly trophies')}
                    count={stampsPanel.close_enough_trophies}
                    done={stampsPanel.close_enough5_done}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div>
          <SectionTitle className="mb-3">{t('Recent Activity')}</SectionTitle>

          {error && (
            <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-2">
              <p className="text-sm text-brand-danger">{error}</p>
            </div>
          )}

          {activity === null ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
              ))}
            </div>
          ) : activity.length === 0 ? (
            <div className="card p-5 text-center">
              <p className="text-sm text-brand-muted">{t('No activity yet. Play a game or write a story to fill this up.')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activity.map((item, i) => (
                <div key={i} className="card p-4 flex items-center gap-3">
                  <img
                    src={GAME_THUMB[item.game_id]}
                    alt={item.game_id}
                    className="w-10 h-10 rounded-2xl object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-brand-text text-sm">{t(item.title)}</p>
                    <p className="text-xs text-brand-muted">{activitySubtitle(item.subtitle, t)}</p>
                  </div>
                  <span className="text-xs text-brand-muted shrink-0">
                    {relativeTime(item.occurred_at, t, lang)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Linked sign-in methods. Shows what's currently attached to
            this auth.users row + lets the user add or remove a method.
            Adding a second method means the user can lose access to
            one provider (Apple revoked, password forgotten) without
            losing the account. */}
        <div className="card p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">{t('Sign-in methods')}</p>
          <ProviderRow
            label={t('Email & password')}
            linked={hasEmail}
            onLink={() => setLinkEmailOpen(true)}
            onUnlink={() => handleUnlink('email')}
            canUnlink={hasEmail && linkedProviders.length > 1}
          />
          <ProviderRow
            label={t('Sign in with Apple')}
            linked={hasApple}
            busy={linkingApple}
            onLink={handleLinkApple}
            onUnlink={() => handleUnlink('apple')}
            canUnlink={hasApple && linkedProviders.length > 1}
          />
          <ProviderRow
            label={t('Sign in with Google')}
            linked={hasGoogle}
            busy={linkingGoogle}
            onLink={handleLinkGoogle}
            onUnlink={() => handleUnlink('google')}
            canUnlink={hasGoogle && linkedProviders.length > 1}
          />
          {linkErr && (
            <p className="text-xs text-brand-danger pt-1 border-t border-brand-border">
              {linkErr}
            </p>
          )}
          {linkEmailOpen && (
            <div className="space-y-2 pt-2 border-t border-brand-border">
              <input
                type="email" autoComplete="email" placeholder={t('Email')}
                value={linkEmailDraft.email}
                onChange={e => setLinkEmailDraft(d => ({ ...d, email: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-brand-border bg-white"
              />
              <input
                type="password" autoComplete="new-password" placeholder={t('Password (≥8 chars)')}
                value={linkEmailDraft.password}
                onChange={e => setLinkEmailDraft(d => ({ ...d, password: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-brand-border bg-white"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleLinkEmail}
                  disabled={linkEmailBusy || !linkEmailDraft.email || linkEmailDraft.password.length < 8}
                  className="flex-1 px-3 py-2 rounded-xl bg-brand-text text-white text-sm font-semibold disabled:opacity-40"
                >
                  {linkEmailBusy ? 'Linking…' : t('Add email login')}
                </button>
                <button
                  onClick={() => { setLinkEmailOpen(false); setLinkEmailDraft({ email: '', password: '' }) }}
                  className="px-3 py-2 rounded-xl border border-brand-border text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          {[
            { path: 'M12 22c1.1 0 2-.9 2-2h-4a2 2 0 002 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z', label: t('Notifications'), onClick: onOpenNotifications },
            { path: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z', label: t('Language'), onClick: onOpenLanguage },
            { path: 'M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z',
              label: tipsReset ? t('Game tips will show again') : t('Show game tips again'),
              onClick: () => {
                if (tipsReset) return
                clearTipFlags()
                  .then(() => setTipsReset(true))
                  .catch(() => { /* offline — row stays tappable */ })
              } },
            { path: 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z', label: 'Privacy', onClick: onOpenPrivacy },
            { path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z', label: t('Help & Support'), onClick: onOpenHelp },
          ].map(item => (
            <button key={item.label}
                    onClick={item.onClick}
                    className="card w-full p-4 flex items-center gap-3 active:bg-brand-elevated
                               transition-colors text-left">
              <svg width="20" height="20" viewBox="0 0 24 24" className="w-7 text-center shrink-0">
                <path d={item.path} fill="#6B7280" />
              </svg>
              <span className="flex-1 font-medium text-brand-text">{item.label}</span>
              <ChevronRight />
            </button>
          ))}
        </div>

        <button onClick={onSignOut}
                className="btn-glass-danger w-full py-3 text-center text-sm font-semibold
                           active:scale-[0.98] transition-transform">{t('Sign Out')}</button>

        <VersionStamp />
      </div>

      {stampsInfoOpen && <StampsInfoOverlay onDismiss={() => setStampsInfoOpen(false)} />}

      {cropSource && (
        <AvatarCropOverlay
          file={cropSource}
          onCancel={() => setCropSource(null)}
          onConfirm={cropped => {
            setCropSource(null)
            handlePickAvatar(cropped)
          }}
        />
      )}
    </div>
  )
}

function ProviderRow({
  label, linked, busy, canUnlink, onLink, onUnlink,
}: {
  label: string
  linked: boolean
  busy?: boolean
  canUnlink: boolean
  onLink: () => void
  onUnlink: () => void
}) {
  return (
    <div className="flex items-center gap-3">
      <span className={
        'w-2 h-2 rounded-full ' + (linked ? 'bg-brand-success' : 'bg-brand-border')
      } />
      <span className="flex-1 text-sm text-brand-text">{label}</span>
      {linked ? (
        <button
          onClick={onUnlink}
          disabled={!canUnlink}
          className="text-xs text-brand-muted active:text-brand-text disabled:opacity-30"
        >
          Remove
        </button>
      ) : (
        <button
          onClick={onLink}
          disabled={busy}
          className="text-xs font-semibold text-brand-text underline disabled:opacity-40"
        >
          {busy ? 'Linking…' : 'Add'}
        </button>
      )}
    </div>
  )
}

/** Tiny inline "?" that opens the stamps explainer. */
function StampsInfoButton({ onOpen }: { onOpen: () => void }) {
  const t = useT()
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('How stamp time works')}
      className="inline-flex items-center justify-center w-4 h-4 ml-1 align-middle
                 rounded-full border border-brand-gold text-brand-gold text-[10px]
                 font-bold leading-none active:scale-90 transition-transform"
    >
      ?
    </button>
  )
}

/** "Sep 10, 2026" in the viewer's timezone, for period-end labels. */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function Stat({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="flex-1 text-center">
      <div className="font-display text-2xl font-bold text-brand-text">
        {value === null ? '…' : value.toLocaleString()}
      </div>
      <div className="text-[11px] text-brand-muted mt-0.5">{label}</div>
    </div>
  )
}

type T = ReturnType<typeof useT>

/** "2h ago" / "yesterday" / "3 days ago" / "Apr 12". */
function relativeTime(iso: string, t: T, lang: string): string {
  const then = new Date(iso).getTime()
  const now  = Date.now()
  const sec  = Math.max(0, Math.floor((now - then) / 1000))
  if (sec < 60)         return t('just now')
  if (sec < 3600)       return t('{n}m ago', { n: Math.floor(sec / 60) })
  if (sec < 86_400)     return t('{n}h ago', { n: Math.floor(sec / 3600) })
  if (sec < 86_400 * 2) return t('yesterday')
  if (sec < 86_400 * 7) return t('{n}d ago', { n: Math.floor(sec / 86_400) })
  return new Date(iso).toLocaleDateString(lang, { month: 'short', day: 'numeric' })
}

/**
 * The activity RPC returns English subtitles in three shapes: a week
 * stamp, a player count, and a score. Re-render each from its parts so
 * the row translates without a server round trip.
 */
function activitySubtitle(raw: string, t: T): string {
  let m = /^Week of (.+)$/.exec(raw)
  if (m) return t('Week of {date}', { date: m[1] })
  m = /^(\d+) players?$/.exec(raw)
  if (m) return m[1] === '1' ? t('1 player') : t('{n} players', { n: m[1] })
  m = /^(\d+) pts$/.exec(raw)
  if (m) return t('{n} pts', { n: m[1] })
  return raw
}

/**
 * One bonus-tracker slot. Progress circles get the stamp in GREY when
 * fulfilled; a reward slot stays a dashed "+1" until the row completes,
 * then shows the stamp in its true red.
 */
function StarterCircle({
  done, label, reward = false, size = 'md',
}: {
  done: boolean
  label?: string
  reward?: boolean
  size?: 'md' | 'sm'
}) {
  const box = size === 'md' ? 'w-12 h-12' : 'w-9 h-9'
  return (
    <div className={`flex flex-col items-center gap-1 ${size === 'md' ? 'w-14' : ''}`}>
      {/* A fulfilled slot is just the stamp on the card's own white:
          the dashed outline and +1 are the "empty" information, and
          the stamp needs no chrome of its own. */}
      <div className={`relative ${box} rounded-full flex items-center justify-center
                       ${done ? '' : 'border-2 border-dashed border-brand-border bg-white'}`}>
        {done ? (
          <>
            <img
              src="/brand/stamp.svg"
              alt=""
              aria-hidden="true"
              draggable={false}
              className={`absolute inset-0 w-full h-full p-1 object-contain select-none
                          pointer-events-none ${reward ? '' : 'grayscale opacity-60'}`}
            />
          </>
        ) : (
          reward && <span className="text-xs font-bold text-brand-muted">+1</span>
        )}
      </div>
      {label && (
        <span className="text-[10px] text-brand-muted text-center leading-tight">{label}</span>
      )}
    </div>
  )
}

/**
 * A five-circle trophy chase: the reward slot on the left, then one
 * circle per required win, greyed-stamped up to `count`. More rows can
 * be added over time; the layout is self-contained.
 */
function ChallengeRow({
  title, count, done,
}: {
  title: string
  count: number
  done: boolean
}) {
  return (
    <div className="mt-4">
      <p className="text-xs text-brand-muted mb-2">{title}</p>
      <div className="flex items-center gap-2.5">
        <StarterCircle reward done={done} size="sm" />
        <div className="w-px self-stretch bg-brand-border" />
        {[0, 1, 2, 3, 4].map(i => (
          <StarterCircle key={i} done={i < Math.min(count, 5)} size="sm" />
        ))}
      </div>
    </div>
  )
}
